import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { scheduleApi } from '../features/schedule/api/scheduleApi';
import {
  EventSchedule,
  EventTemplate,
  GetSchedulesResponse,
} from '../features/schedule/api/types';
import i18n from '../i18n';
import { notificationStore } from './NotificationStore';

export class ScheduleStore {
  @observable schedules: EventSchedule[] = [];
  @observable templates: EventTemplate[] = [];
  @observable currentSchedule: EventSchedule | null = null;
  @observable isLoading = false;
  @observable isLoadingTemplates = false;
  @observable error: Error | null = null;

  constructor() {
    makeObservable(this);
  }

  // ===============================
  // COMPUTED PROPERTIES
  // ===============================

  @computed get isError(): boolean {
    return this.error !== null;
  }

  @computed get hasData(): boolean {
    return this.schedules.length > 0;
  }

  @computed get hasTemplatesData(): boolean {
    return this.templates.length > 0;
  }

  @computed get hasCurrentSchedule(): boolean {
    return this.currentSchedule !== null;
  }

  @computed get currentScheduleId(): string | null {
    return this.currentSchedule?.id || null;
  }

  @computed get currentScheduleName(): string | null {
    return this.currentSchedule?.name || null;
  }

  /**
   * Get the schedule that is active for a specific date
   */
  getScheduleForDate(date: Date): EventSchedule | null {
    const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format

    return (
      this.schedules.find((schedule) => {
        const startDate = new Date(schedule.startDate);
        const endDate = new Date(schedule.endDate);
        const targetDate = new Date(dateString);

        return targetDate >= startDate && targetDate <= endDate;
      }) || null
    );
  }

  /**
   * Get schedule by ID
   */
  getScheduleById(scheduleId: string): EventSchedule | null {
    return (
      this.schedules.find((schedule) => schedule.id === scheduleId) || null
    );
  }

  /**
   * Get the current schedule based on today's date
   */
  @computed get currentScheduleForToday(): EventSchedule | null {
    return this.getScheduleForDate(new Date());
  }

  /**
   * Get templates for a specific schedule
   */
  getTemplatesForSchedule(scheduleId: string): EventTemplate[] {
    return this.templates.filter(
      (template) => template.scheduleId === scheduleId
    );
  }

  /**
   * Get templates for a specific date (based on day of week)
   */
  getTemplatesForDate(date: Date): EventTemplate[] {
    const dayOfWeek = date.getDay(); // 0 (Sunday) - 6 (Saturday)
    const schedule = this.getScheduleForDate(date);

    if (!schedule) return [];

    return this.templates.filter(
      (template) =>
        template.scheduleId === schedule.id && template.dayOfWeek === dayOfWeek
    );
  }

  // ===============================
  // ACTIONS
  // ===============================

  @action async loadSchedules(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const response: GetSchedulesResponse = await scheduleApi.getSchedules({
        // active: true,
      });
      this.schedules = response.schedules;

      // Auto-select the most recent active schedule
      this.selectMostRecentActiveSchedule();

      // Load templates for all schedules
      await this.loadTemplates();
    } catch (error) {
      this.error = error as Error;
      notificationStore.showError(i18n.t('errors.failedToLoadSchedules'));
    } finally {
      this.isLoading = false;
    }
  }

  async loadTemplates(): Promise<void> {
    if (this.isLoadingTemplates || this.schedules.length === 0) {
      return;
    }
    runInAction(() => {
      this.isLoadingTemplates = true;
    });

    try {
      const scheduleIds = this.schedules.map((schedule) => schedule.id);

      const response = await scheduleApi.getTemplatesBySchedule(scheduleIds);

      runInAction(() => {
        this.templates = response.templates;
      });
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Don't show error notification for templates as they're not critical
    } finally {
      runInAction(() => {
        this.isLoadingTemplates = false;
      });
    }
  }

  @action selectSchedule(scheduleId: string): void {
    const schedule = this.schedules.find((s) => s.id === scheduleId);
    if (schedule) {
      this.currentSchedule = schedule;
    }
  }

  @action selectMostRecentActiveSchedule(): void {
    const mostRecent = this.schedules[this.schedules.length - 1];
    if (mostRecent) {
      this.currentSchedule = mostRecent;
    }
  }

  @action clear(): void {
    this.schedules = [];
    this.templates = [];
    this.currentSchedule = null;
    this.isLoading = false;
    this.isLoadingTemplates = false;
    this.error = null;
  }

  // ===============================
  // LIFECYCLE METHODS
  // ===============================

  async initialize(): Promise<void> {
    await this.loadSchedules();
  }

  destroy(): void {
    this.clear();
  }
}

// Export singleton instance
export const scheduleStore = new ScheduleStore();
